import React, { createContext, useContext, useEffect, useState } from "react";
import { api, applyPriceTiers, getBundleTier } from "../lib/api";
import { t as translate } from "../lib/i18n";

const AppCtx = createContext(null);

export const useApp = () => useContext(AppCtx);

const loadCart = () => {
  try {
    return JSON.parse(localStorage.getItem("hf_cart") || "[]");
  } catch {
    return [];
  }
};

// Composite cart-line identity: same product with different pack sizes are separate lines
const lineKey = (it) => `${it.product_id}|${it.bundle_qty || 0}`;

export const AppProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("hf_lang") || "id");
  const [currency, setCurrency] = useState(() => localStorage.getItem("hf_currency") || "IDR");
  const [cart, setCart] = useState(loadCart);
  const [settings, setSettings] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [customer, setCustomer] = useState(() => {
    try { return JSON.parse(localStorage.getItem("hf_customer") || "null"); } catch { return null; }
  });

  useEffect(() => {
    localStorage.setItem("hf_lang", lang);
  }, [lang]);
  useEffect(() => {
    localStorage.setItem("hf_currency", currency);
  }, [currency]);
  useEffect(() => {
    localStorage.setItem("hf_cart", JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setSettings(r.data);
      // Dynamic favicon: convert logo to PNG via canvas so Safari renders it too (Safari ignores JPEG favicons)
      const logo = r.data?.logo_url;
      if (logo) {
        const fav = document.getElementById("dynamic-favicon");
        if (fav) {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            try {
              const canvas = document.createElement("canvas");
              canvas.width = 64; canvas.height = 64;
              canvas.getContext("2d").drawImage(img, 0, 0, 64, 64);
              fav.href = canvas.toDataURL("image/png");
            } catch {
              // CORS-blocked or other error: keep the static PNG favicon
            }
          };
          img.onerror = () => {};
          img.src = logo;
        }
      }
      if (r.data?.site_name) document.title = `${r.data.site_name} — Handmade Christmas`;
    }).catch(() => {});
    const token = localStorage.getItem("hf_admin_token");
    if (token) {
      api.get("/auth/me").then((r) => setAdmin(r.data)).catch(() => {
        localStorage.removeItem("hf_admin_token");
      });
    }
    // Customer auth: validate token + sync customer from localStorage
    const ctok = localStorage.getItem("hf_customer_token");
    if (ctok) {
      // Need separate axios call with customer token (admin interceptor uses admin token)
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customer/me`, { headers: { Authorization: `Bearer ${ctok}` } })
        .then((r) => r.ok ? r.json() : Promise.reject())
        .then((c) => { setCustomer(c); localStorage.setItem("hf_customer", JSON.stringify(c)); })
        .catch(() => { localStorage.removeItem("hf_customer_token"); localStorage.removeItem("hf_customer"); setCustomer(null); });
    }
    const handler = () => {
      try { setCustomer(JSON.parse(localStorage.getItem("hf_customer") || "null")); } catch {}
    };
    window.addEventListener("hf:customer-changed", handler);
    return () => window.removeEventListener("hf:customer-changed", handler);
  }, []);

  // Recompute price for a cart line at a given quantity.
  // Bundle lines: price = TOTAL pack price (per pack). Unit lines: wholesale per-pc tiers.
  const withTierPrice = (item, qty) => {
    if (item.bundle_qty) {
      const tier = getBundleTier(item, item.bundle_qty);
      if (tier) return { ...item, price_idr: +tier.price_idr, price_usd: +tier.price_usd, quantity: qty };
      return { ...item, quantity: qty };
    }
    const p = applyPriceTiers(item, qty);
    return { ...item, price_idr: p.price_idr, price_usd: p.price_usd, quantity: qty };
  };

  const addToCart = (product, qty = 1, bundleQty = 0) => {
    setCart((prev) => {
      const key = `${product.id}|${bundleQty}`;
      const idx = prev.findIndex((it) => lineKey(it) === key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = withTierPrice(next[idx], next[idx].quantity + qty);
        return next;
      }
      const baseName = product[`name_${lang}`] || product.name_en;
      const base = {
        product_id: product.id,
        slug: product.slug,
        name: bundleQty > 1 ? `${baseName} (${lang === "id" ? "Paket" : "Pack"} ${bundleQty} pcs)` : baseName,
        image: product.images?.[0] || "",
        price_idr: product.price_idr,
        price_usd: product.price_usd,
        price_tiers: product.price_tiers || [],
        pricing_mode: product.pricing_mode || "unit",
        bundle_qty: bundleQty,
        weight_grams: product.weight_grams || 200,
        quantity: 0,
      };
      return [...prev, withTierPrice(base, qty)];
    });
  };

  const updateQty = (item, quantity) => {
    const key = lineKey(item);
    setCart((prev) =>
      prev
        .map((it) => (lineKey(it) === key ? withTierPrice(it, quantity) : it))
        .filter((it) => it.quantity > 0)
    );
  };

  const removeFromCart = (item) => {
    const key = lineKey(item);
    setCart((prev) => prev.filter((it) => lineKey(it) !== key));
  };

  const clearCart = () => setCart([]);

  // Count actual pcs (bundle lines count as quantity × pcs per pack)
  const cartCount = cart.reduce((s, it) => s + it.quantity * (it.bundle_qty || 1), 0);
  const cartSubtotalIdr = cart.reduce((s, it) => s + it.price_idr * it.quantity, 0);
  const cartSubtotalUsd = cart.reduce((s, it) => s + it.price_usd * it.quantity, 0);
  const cartWeight = cart.reduce((s, it) => s + (it.weight_grams || 200) * (it.bundle_qty || 1) * it.quantity, 0);

  const t = (key) => translate(lang, key);

  // Selecting a language auto-switches currency (ID→IDR, EN→USD). Currency buttons still allow manual override.
  const setLangWithCurrency = (l) => {
    setLang(l);
    setCurrency(l === "id" ? "IDR" : "USD");
  };

  const loginAdmin = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("hf_admin_token", data.access_token);
    setAdmin(data.user);
    return data.user;
  };
  const logoutAdmin = () => {
    localStorage.removeItem("hf_admin_token");
    setAdmin(null);
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const customerLogin = async (email, password) => {
    const { data } = await api.post("/customer/login", { email, password });
    localStorage.setItem("hf_customer_token", data.access_token);
    localStorage.setItem("hf_customer", JSON.stringify(data.customer));
    setCustomer(data.customer);
    return data.customer;
  };
  const customerRegister = async (payload) => {
    const { data } = await api.post("/customer/register", payload);
    localStorage.setItem("hf_customer_token", data.access_token);
    localStorage.setItem("hf_customer", JSON.stringify(data.customer));
    setCustomer(data.customer);
    return data.customer;
  };
  const logoutCustomer = async () => {
    const ctok = localStorage.getItem("hf_customer_token");
    if (ctok) {
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/customer/auth/logout`, {
          method: "POST",
          headers: { Authorization: `Bearer ${ctok}` },
        });
      } catch {}
    }
    localStorage.removeItem("hf_customer_token");
    localStorage.removeItem("hf_customer");
    setCustomer(null);
  };

  return (
    <AppCtx.Provider
      value={{
        lang, setLang, setLangWithCurrency,
        currency, setCurrency,
        cart, addToCart, updateQty, removeFromCart, clearCart,
        cartCount, cartSubtotalIdr, cartSubtotalUsd, cartWeight,
        lineKey,
        settings, setSettings,
        admin, loginAdmin, logoutAdmin,
        customer, customerLogin, customerRegister, logoutCustomer,
        t,
      }}
    >
      {children}
    </AppCtx.Provider>
  );
};
